import React from 'react';

/**
 * Props for QuestionOptionInput
 * - options: array of { text: string, image?: string }
 * - type: question type
 * - onChange: (updatedOptions) => void
 * - canEdit: boolean
 */
export interface OptionItem {
  text: string;
  image?: string;
}

interface QuestionOptionInputProps {
  options: OptionItem[];
  type: string;
  onChange: (options: OptionItem[]) => void;
  canEdit: boolean;
}

const QuestionOptionInput: React.FC<QuestionOptionInputProps> = ({ options, type, onChange, canEdit }) => {
  // Handlers for text/image changes
  const handleTextChange = (idx: number, value: string) => {
    const updated = options.map((opt, i) => i === idx ? { ...opt, text: value } : opt);
    onChange(updated);
  };
  const handleImageChange = (idx: number, file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const updated = options.map((opt, i) => i === idx ? { ...opt, image: e.target?.result as string } : opt);
      onChange(updated);
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveImage = (idx: number) => {
    const updated = options.map((opt, i) => i === idx ? { ...opt, image: undefined } : opt);
    onChange(updated);
  };
  const handleAddOption = () => {
    // For matching type, add a pair (two items) at once
    if (type === 'Matching') {
      onChange([...options, { text: '', image: undefined }, { text: '', image: undefined }]);
    } else {
      onChange([...options, { text: '', image: undefined }]);
    }
  };
  const handleRemoveOption = (idx: number) => {
    if (type === 'Matching') {
      // For matching type, determine if we're removing from left or right column
      const half = Math.ceil(options.length / 2);
      if (idx < half) {
        // Removing from left column - remove corresponding right item too
        const rightIdx = idx + half < options.length ? idx + half : -1;
        if (rightIdx === -1) {
          // No matching right item, just remove the left item
          onChange(options.filter((_, i) => i !== idx));
        } else {
          // Remove both left and right items
          onChange(options.filter((_, i) => i !== idx && i !== rightIdx));
        }
      } else {
        // Removing from right column - remove corresponding left item too
        const leftIdx = idx - half;
        onChange(options.filter((_, i) => i !== idx && i !== leftIdx));
      }
    } else {
      // For other question types, just remove the single option
      onChange(options.filter((_, i) => i !== idx));
    }
  };

  // Matching type: two columns
  if (type === 'Matching') {
    const half = Math.ceil(options.length / 2);
    const left = options.slice(0, half);
    const right = options.slice(half);
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="font-semibold text-gray-600 mb-2">Column A</div>
            {left.map((opt, idx) => (
              <div className="flex items-center mb-2" key={idx}>
                <input
                  type="text"
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder={`A${idx + 1}`}
                  value={opt.text}
                  onChange={e => handleTextChange(idx, e.target.value)}
                  disabled={!canEdit}
                />
                {canEdit && (
                  <button type="button" className="ml-2 text-red-500" onClick={() => handleRemoveOption(idx)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          <div>
            <div className="font-semibold text-gray-600 mb-2">Column B</div>
            {right.map((opt, idx) => (
              <div className="flex items-center mb-2" key={half + idx}>
                <input
                  type="text"
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder={`B${idx + 1}`}
                  value={opt.text}
                  onChange={e => handleTextChange(half + idx, e.target.value)}
                  disabled={!canEdit}
                />
                {canEdit && (
                  <button type="button" className="ml-2 text-red-500" onClick={() => handleRemoveOption(half + idx)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col space-y-2">
            <button type="button" className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark" onClick={handleAddOption}>
              Add Pair
            </button>
            <p className="text-xs text-gray-500">Adding or removing an item will affect its matching pair</p>
          </div>
        )}
      </div>
    );
  }

  // Default (MultipleChoice, MultipleSelection, etc): text + image
  return (
    <div className="space-y-2">
      {options.map((opt, idx) => (
        <div className="flex items-center gap-2" key={idx}>
          <input
            type="text"
            className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder={`Option ${idx + 1}`}
            value={opt.text}
            onChange={e => handleTextChange(idx, e.target.value)}
            disabled={!canEdit}
          />
          <input
            type="file"
            accept="image/*"
            onChange={e => handleImageChange(idx, e.target.files?.[0] || null)}
            disabled={!canEdit}
          />
          {opt.image && (
            <div className="relative">
              <img src={opt.image} alt={`Option ${idx + 1} preview`} className="w-12 h-12 object-contain border rounded" />
              {canEdit && (
                <button type="button" className="absolute top-0 right-0 bg-white rounded-full text-red-500 p-1" onClick={() => handleRemoveImage(idx)}>
                  ×
                </button>
              )}
            </div>
          )}
          {canEdit && (
            <button type="button" className="ml-2 text-red-500" onClick={() => handleRemoveOption(idx)}>
              Remove
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <button type="button" className="mt-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark" onClick={handleAddOption}>
          Add Option
        </button>
      )}
    </div>
  );
};

export default QuestionOptionInput;
